import { useState } from 'react';
import { Check, Copy, Eye, EyeOff, KeyRound, ExternalLink } from 'lucide-react';
import { Button } from '@/components/button';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { useToast } from '@/components/toast';
import { usePortalCredentials, useRevealCredential } from '@/lib/portal-vault';
import { formatStatus } from '@/lib/format';
import type { CredentialData, CredentialRevealedData } from '@/types/api';

export function PortalVault() {
  const { data: credentials, isLoading } = usePortalCredentials();

  return (
    <div className="space-y-5">
      <PageHeader
        icon={KeyRound}
        title="Passwords"
        subtitle="Logins and secrets Mehrutherm securely stores for you. Revealing one is logged."
      />

      {isLoading ? (
        <SkeletonList rows={4} />
      ) : !credentials || credentials.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No saved passwords"
          description="When Mehrutherm stores a login or secret for you, it'll appear here to reveal when you need it."
        />
      ) : (
        <DataList>
          {credentials.map((credential) => (
            <VaultRow key={credential.id} credential={credential} />
          ))}
        </DataList>
      )}
    </div>
  );
}

function VaultRow({ credential }: { credential: CredentialData }) {
  const reveal = useRevealCredential();
  const [revealed, setRevealed] = useState<CredentialRevealedData | null>(null);
  const toast = useToast();

  async function toggle() {
    if (revealed) {
      setRevealed(null);
      return;
    }
    try {
      setRevealed(await reveal.mutateAsync(credential.id));
    } catch {
      toast.error('Could not reveal this secret.');
    }
  }

  return (
    <DataListRow interactive={false} className="flex-col items-stretch gap-2 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-surface-muted)] text-[var(--color-foreground-muted)]">
          <KeyRound className="size-4" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-foreground">{credential.label}</span>
            <span className="shrink-0 rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              {formatStatus(credential.type)}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            {credential.username ? <span className="truncate">{credential.username}</span> : null}
            {credential.url ? (
              <a
                href={credential.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
              >
                <ExternalLink className="size-3" /> Open
              </a>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {revealed ? <SecretField value={revealed.value} /> : null}
        <Button size="sm" variant="secondary" onClick={toggle} disabled={reveal.isPending}>
          {revealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
          {revealed ? 'Hide' : reveal.isPending ? 'Revealing…' : 'Reveal'}
        </Button>
      </div>
    </DataListRow>
  );
}

function SecretField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  }

  return (
    <div className="flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-2 py-1">
      <code className="max-w-[220px] truncate font-mono text-xs text-foreground">{value}</code>
      <button
        type="button"
        onClick={copy}
        aria-label="Copy secret"
        className="inline-flex size-6 items-center justify-center rounded text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]"
      >
        {copied ? <Check className="size-3.5 text-[var(--success)]" /> : <Copy className="size-3.5" />}
      </button>
    </div>
  );
}
